import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import useAvisosReservas from '../hooks/useAvisosReservas'
import './Admin.css'

const API = process.env.REACT_APP_API_URL || ''

const hoy = () => new Date().toISOString().split('T')[0]

function VistaDia({ token, refreshKey, idsNuevas }) {
  const [fecha, setFecha] = useState(hoy())
  const [reservas, setReservas] = useState([])
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState('')

  const cargar = async (f) => {
    setCargando(true); setError('')
    try {
      const res = await axios.get(`${API}/api/admin/reservas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { fecha: f, per_page: 100 }
      })
      setReservas(res.data.reservas || [])
    } catch {
      setError('No se pudieron cargar las reservas del día.')
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => { cargar(fecha) }, [fecha, refreshKey])

  const porHora = reservas.reduce((acc, r) => {
    const hora = r.hora_inicio || r.hora
    if (!acc[hora]) acc[hora] = []
    acc[hora].push(r)
    return acc
  }, {})

  const horasOrdenadas = Object.keys(porHora).sort()
  const total = reservas.length
  const totalPersonas = reservas.reduce((s, r) => s + r.personas, 0)
  const pendientes = reservas.filter(r => r.estado === 'pendiente').length

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <input
          type="date"
          value={fecha}
          onChange={e => setFecha(e.target.value)}
          style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '0.6rem 1rem', fontFamily: 'Montserrat, sans-serif', fontSize: '0.75rem' }}
        />
        <div style={{ display: 'flex', gap: '2rem' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{total} reserva{total !== 1 ? 's' : ''}</span>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{totalPersonas} comensales</span>
          {pendientes > 0 && <span style={{ fontSize: '0.7rem', color: 'var(--gold)' }}>{pendientes} pendiente{pendientes !== 1 ? 's' : ''}</span>}
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: '1rem' }}>{error}</div>}
      {cargando ? (
        <div className="loading" style={{ height: 150 }}>Cargando...</div>
      ) : reservas.length === 0 ? (
        <div className="empty-state">No hay reservas para este día</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {horasOrdenadas.map(hora => (
            <div key={hora}>
              <div style={{ fontSize: '0.7rem', color: 'var(--gold)', letterSpacing: '0.2em', marginBottom: '0.75rem', paddingBottom: '0.4rem', borderBottom: '1px solid var(--border)' }}>
                {hora}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {porHora[hora].map(r => (
                  <div key={r.id} className={idsNuevas.has(r.id) ? 'reserva-nueva' : ''} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', background: 'var(--dark-2)', border: '1px solid var(--border)', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 150 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{r.nombre_cliente}</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{r.email_cliente}{r.telefono_cliente ? ` · ${r.telefono_cliente}` : ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{r.personas} pers.</span>
                      {r.mesa && <span style={{ fontSize: '0.65rem', color: 'var(--gold)', border: '1px solid var(--border)', padding: '0.2rem 0.5rem' }}>{r.mesa.numero}</span>}
                      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{r.ubicacion}</span>
                      {r.tipo === 'salon_privado' && <span style={{ fontSize: '0.6rem', color: '#9b59b6', border: '1px solid rgba(155,89,182,0.3)', padding: '0.2rem 0.5rem' }}>VIP</span>}
                      <span className={`badge badge-${r.estado}`}>{r.estado}</span>
                    </div>
                    {r.notas && <div style={{ width: '100%', fontSize: '0.65rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>"{r.notas}"</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Admin() {
  const [vista, setVista] = useState('reservas')  // 'reservas' | 'dia'
  const [reservas, setReservas] = useState([])
  const [filtro, setFiltro] = useState('')
  const [cargando, setCargando] = useState(true)
  const [errorCarga, setErrorCarga] = useState('')
  const [modal, setModal] = useState(null)
  const [pagina, setPagina] = useState(1)
  const [totalPaginas, setTotalPaginas] = useState(1)
  const [total, setTotal] = useState(0)

  const token = localStorage.getItem('token')

  // Avisos de reservas nuevas (sonido + notificación + recarga automática)
  const [refreshKey, setRefreshKey] = useState(0)
  const [idsNuevas, setIdsNuevas] = useState(() => new Set())
  const [avisoBanner, setAvisoBanner] = useState(null)   // { n, nombre }

  const onNuevas = useCallback((nuevas) => {
    setIdsNuevas(prev => new Set([...prev, ...nuevas.map(r => r.id)]))
    setAvisoBanner({ n: nuevas.length, nombre: nuevas[0]?.nombre_cliente })
    setRefreshKey(k => k + 1)
  }, [])

  const { activos, activar, desactivar, permisoNotif } = useAvisosReservas(token, onNuevas)

  const cargar = async (estado = '', pag = 1) => {
    setCargando(true)
    setErrorCarga('')
    try {
      const params = new URLSearchParams({ page: pag })
      if (estado) params.set('estado', estado)
      const res = await axios.get(`${API}/api/admin/reservas?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      setReservas(res.data.reservas)
      setTotalPaginas(res.data.paginas)
      setTotal(res.data.total)
    } catch {
      setErrorCarga('No se pudo conectar con el servidor. Comprueba que el backend está en marcha.')
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => { cargar() }, [])
  useEffect(() => { if (refreshKey > 0) cargar(filtro, pagina) }, [refreshKey])

  const abrirModal = (reserva, accion) => setModal({ reserva, accion })
  const cerrarModal = () => setModal(null)

  const confirmarAccion = async () => {
    try {
      await axios.put(`${API}/api/admin/reservas/${modal.reserva.id}/estado`, { estado: modal.accion }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setReservas(reservas.map(r => r.id === modal.reserva.id ? { ...r, estado: modal.accion } : r))
      cerrarModal()
    } catch {
      setErrorCarga('No se pudo actualizar el estado. Inténtalo de nuevo.')
      cerrarModal()
    }
  }

  const handleFiltro = (estado) => { setFiltro(estado); setPagina(1); cargar(estado, 1) }
  const handlePagina = (pag) => { setPagina(pag); cargar(filtro, pag) }
  const pendientes = reservas.filter(r => r.estado === 'pendiente').length

  return (
    <div className="page">
      <div className="page-header">
        <p className="page-label">Panel de administración</p>
        <h1 className="page-title">Reservas</h1>
        <div className="page-divider"></div>
        {pendientes > 0 && vista === 'reservas' && (
          <p style={{ color: 'var(--gold)', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
            {pendientes} reserva{pendientes > 1 ? 's' : ''} pendiente{pendientes > 1 ? 's' : ''} de confirmar
          </p>
        )}
        <div className="avisos-barra">
          <button
            type="button"
            className={`avisos-btn ${activos ? 'on' : ''}`}
            onClick={activos ? desactivar : activar}
            title={activos ? 'Desactivar sonido y notificaciones' : 'Activar sonido y notificaciones de reservas nuevas'}
          >
            {activos ? '🔔 Avisos activados' : '🔕 Activar avisos'}
          </button>
          <span className="avisos-info">
            {activos
              ? (permisoNotif === 'granted'
                  ? 'Sonará y avisará cuando entre una reserva (con esta pestaña abierta).'
                  : permisoNotif === 'denied'
                    ? 'Sonará al entrar una reserva. Las notificaciones están bloqueadas en el navegador.'
                    : 'Sonará al entrar una reserva.')
              : 'El panel se actualiza solo cada 30 s. Activa los avisos para que suene.'}
          </span>
        </div>
        {avisoBanner && (
          <div className="avisos-banner" onClick={() => setAvisoBanner(null)}>
            {avisoBanner.n === 1
              ? `Nueva reserva de ${avisoBanner.nombre}`
              : `${avisoBanner.n} reservas nuevas`}
            <span className="avisos-banner-x">✕</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
          <Link to="/admin/encuestas" style={{ fontSize: '0.6rem', color: 'var(--text-muted)', letterSpacing: '0.15em', textTransform: 'uppercase', textDecoration: 'none' }}>
            Ver encuestas →
          </Link>
          <Link to="/admin/ajustes" style={{ fontSize: '0.6rem', color: 'var(--text-muted)', letterSpacing: '0.15em', textTransform: 'uppercase', textDecoration: 'none' }}>
            Datos del bar (teléfono, horario…) →
          </Link>
        </div>
      </div>

      {/* Tabs de vista */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '2rem', borderBottom: '1px solid var(--border)' }}>
        {[['reservas', 'Todas las reservas'], ['dia', 'Vista del día']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setVista(id)}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              border: 'none',
              borderBottom: vista === id ? '2px solid var(--gold)' : '2px solid transparent',
              color: vista === id ? 'var(--gold)' : 'var(--text-muted)',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '0.65rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              marginBottom: '-1px',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {vista === 'dia' ? (
        <VistaDia token={token} refreshKey={refreshKey} idsNuevas={idsNuevas} />
      ) : (
        <>
          <div className="admin-filtros">
            {['', 'pendiente', 'confirmada', 'cancelada'].map(e => (
              <button key={e} className={`filtro-btn ${filtro === e ? 'active' : ''}`} onClick={() => handleFiltro(e)}>
                {e === '' ? 'Todas' : e.charAt(0).toUpperCase() + e.slice(1)}
              </button>
            ))}
          </div>

          {errorCarga && <div className="error-msg" style={{ marginBottom: '1.5rem' }}>{errorCarga}</div>}
          {cargando ? <div className="loading" style={{ height: '200px' }}>Cargando...</div> : (
            <>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                {total} reserva{total !== 1 ? 's' : ''} en total
              </p>
              <div className="admin-tabla">
                {reservas.length === 0 ? (
                  <div className="empty-state">No hay reservas</div>
                ) : reservas.map(r => (
                  <div key={r.id} className={`admin-fila ${idsNuevas.has(r.id) ? 'reserva-nueva' : ''}`}>
                    <div className="admin-cliente">
                      <div className="admin-nombre">{r.nombre_cliente}</div>
                      <div className="admin-email">{r.email_cliente}{r.telefono_cliente ? ` · ${r.telefono_cliente}` : ''}</div>
                    </div>
                    <div className="admin-detalles">
                      <span>{new Date(r.fecha + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                      <span>{r.hora_inicio || r.hora}</span>
                      <span>{r.personas} personas</span>
                      {r.mesa && <span style={{ color: 'var(--gold)', fontSize: '0.65rem' }}>Mesa {r.mesa.numero}</span>}
                      {r.notas && <span className="admin-notas">"{r.notas}"</span>}
                    </div>
                    <div className="admin-acciones">
                      <span className={`badge badge-${r.estado}`}>{r.estado}</span>
                      <div className="admin-btns">
                        {r.estado !== 'confirmada' && (
                          <button className="btn-secondary" style={{ fontSize: '0.55rem', padding: '0.4rem 0.8rem' }} onClick={() => abrirModal(r, 'confirmada')}>Confirmar</button>
                        )}
                        {r.estado !== 'cancelada' && (
                          <button className="btn-danger" style={{ fontSize: '0.55rem', padding: '0.4rem 0.8rem' }} onClick={() => abrirModal(r, 'cancelada')}>Cancelar</button>
                        )}
                        {r.estado !== 'pendiente' && (
                          <button className="btn-secondary" style={{ fontSize: '0.55rem', padding: '0.4rem 0.8rem' }} onClick={() => abrirModal(r, 'pendiente')}>Pendiente</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {totalPaginas > 1 && (
                <div className="admin-paginacion">
                  <button className="btn-secondary" onClick={() => handlePagina(pagina - 1)} disabled={pagina === 1}>Anterior</button>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{pagina} / {totalPaginas}</span>
                  <button className="btn-secondary" onClick={() => handlePagina(pagina + 1)} disabled={pagina === totalPaginas}>Siguiente</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {modal && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={cerrarModal}>✕</button>
            <div className={`modal-icon ${modal.accion === 'confirmada' ? 'icon-ok' : modal.accion === 'cancelada' ? 'icon-cancel' : 'icon-pending'}`}>
              {modal.accion === 'confirmada' ? '✓' : modal.accion === 'cancelada' ? '✕' : '○'}
            </div>
            <h2 className="modal-title">
              {modal.accion === 'confirmada' ? '¿Confirmar reserva?' : modal.accion === 'cancelada' ? '¿Cancelar reserva?' : '¿Marcar como pendiente?'}
            </h2>
            <div className="modal-info">
              <p><strong>{modal.reserva.nombre_cliente}</strong></p>
              <p>{new Date(modal.reserva.fecha + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
              <p>{modal.reserva.hora_inicio || modal.reserva.hora} · {modal.reserva.personas} personas</p>
            </div>
            <div className="modal-btns">
              <button className="btn-secondary" onClick={cerrarModal}>Volver</button>
              <button
                className={modal.accion === 'cancelada' ? 'btn-danger' : 'btn-primary'}
                style={{ width: 'auto', padding: '0.8rem 2rem' }}
                onClick={confirmarAccion}
              >
                {modal.accion === 'confirmada' ? 'Sí, confirmar' : modal.accion === 'cancelada' ? 'Sí, cancelar' : 'Sí, marcar pendiente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
